import { el, setChildren } from 'redom';

import { debounce } from 'lodash';

import InternetPhone from 'internet-phone';
import application from 'application';
import serverInterface from 'server-interface';
import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import applicationEventCodes from 'application-event-codes';
import internalEventCodes from 'event-system/codes/internal';
import { formatToCompactDateV2 } from 'utilities/date';

import { renderV2 as messageEditorRenderer } from 'root/startup-view/inbox/messages-panel/message-editor';
import { ASSISTANT_MENTION_TYPES } from 'generics/components/basic-text-editor-slate/elements/mention/mention-types';
import {
  getMessageFragment
} from 'root/startup-view/inbox/messages-panel/utilities';

import {
  buildMessageContext,
  shouldInterceptLink,
  customLinkHandler,
  canAcceptFeedbackForMessage
} from './utilities';

import EditorSelectionPreview from './editor-selection-preview';
import FeedbackForm from './feedback-form';

import './index.scss';

let conversation = null;
let internetPhone = null;

class Assistant {

  constructor(options = {}) {

    this.data = null;
    this.displayedData = null;
    this.expanded = true;
    this.feedbackFormDisplayed = false;
    this.previousWidth = localStorage.getItem('assistant-width') || '400px';
    this.editorSelectionPreview = new EditorSelectionPreview({
      onClear: this.clearPreviewState.bind(this),
      onShow: function onPreviewShow() {
        if (internetPhone !== null) {
          internetPhone.hideSuggestions();
        }
      },
      onHide: function onPreviewHide() {
        if (internetPhone !== null) {
          internetPhone.showSuggestions();
        }
      }
    });
    this.el = el('#startup-assistant-main', {
      onclick: this.handleLinkClicks.bind(this),
      class: 'loading',
    }, [
      el('ht-spinner', { size: 'small' })
    ]);
  }

  handleLinkClicks(event) {
    const link = event.target.closest('a.link') || event.linkTarget;
    if (!link) return;

    if (shouldInterceptLink(link, event)) {
      event.preventDefault();
      customLinkHandler(link);
    }
  };

  setDialogCallbacks(callbacks) {
    this.onClose = callbacks.onClose;
  }

  renderInternetPhone() {

    internetPhone = new InternetPhone(this.getInternetPhoneConfiguration());

    internetPhone.update(conversation);

    return internetPhone;

  }

  getInternetPhoneConfiguration() {

    const configuration = {
      conversationId: conversation._id,
      observerId: application.startupUserObserver.getObserverId(),
      eventSystem: application.eventSystem,
      internalEventCodes,
      serverInterface: {
        setConversationMessageAsViewed: (...parameters) => serverInterface.startup.helpdesk.setConversationMessageAsViewed(...parameters),
        getPaginatedConversationMessagesByConversationIdAndQueryOptions: (...parameters) => serverInterface.startup.helpdesk.getPaginatedConversationMessagesByConversationIdAndQueryOptions(...parameters),
        getEmailConversationMessageById: (...parameters) => serverInterface.startup.helpdesk.getEmailConversationMessageById(...parameters),
      },
      editorRenderer(mountPoint, configuration) {

        configuration.attachments = configuration.conversation.attachments;
        return messageEditorRenderer(mountPoint, {
          ...configuration,
          mentionTypes: ASSISTANT_MENTION_TYPES,
          author: application.getUser()
        });

      },
      createDraft: this.createMessageDraft.bind(this),
      onMessageContentChange: debounce(this.updateMessageContent.bind(this), 500),
      publish: this.publish.bind(this),
      uploadAttachment: this.inlineAttachment.bind(this),
      createAttachment: this.uploadAttachment.bind(this),
      deleteAttachment: this.deleteAttachment.bind(this),
      onAllUnviewedMessagesViewed() {},
      onNewUnviewedMessage() {},
      utilities: {
        dateTimeFormatter: formatToCompactDateV2
      },
      onActivity: this.handleActivity.bind(this),
      disableAILevel: true,
      displayCopyButton: true,
      onMessageCopied() {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Copied',
          seconds: 5,
          type: 'info'
        });
      },
      enableSuggestions: true,
      onSuggestionSelect() {
        application.platformUserObserver.pageViewObserver.emit('Assistant suggestions used');
      },
      feedbacks: {
        canAccept(message) {
          return canAcceptFeedbackForMessage(message);
        },
        showForm: this.renderFeedbackForm.bind(this)
      },
      editorSelectionPreview: this.editorSelectionPreview,
      blockPreviewRenderer: function createBlockPreview() {
        return new EditorSelectionPreview({ maxHeight: 120 });
      },
      enableQuotes: true
    };

    return configuration;

  }

  renderFeedbackForm(message) {

    if (this.feedbackFormDisplayed === true) {
      return;
    }

    this.feedbackFormDisplayed = true;

    application.dialogsManager.displayWithHeader(FeedbackForm, {
      title: 'Submit Feedback',
      contentHolderClassName: 'feedback-form-dialog-content',
      contentConfiguration: {
        message
      }
    }).then(function onDialogComplete() {
      this.feedbackFormDisplayed = false;
    }.bind(this));

  }

  async createMessageDraft(conversationId) {

    const result = await serverInterface.startup.helpdesk.getOrCreateConversationMessageDraftForHelpProvider(conversationId);

    return result;

  }

  async updateMessageContent(conversationId, conversationMessageId, content) {

    const result = await serverInterface.startup.helpdesk.updateConversationMessageContentById(conversationId, conversationMessageId, { content });

    return result;

  }

  async publish(conversationId, conversationMessageId, content, receivers, lastMessage = null, quotedMessageId = null) {

    if (basicEditorValueSerializer.utilities.isEmpty(content) === true) {
      return;
    }

    const payload = {
      content,
      receivers,
      subject: `Re: ${conversation.subject}`,
      metadata: {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        localDateTime: new Date(),
        activeContext: {
          ...buildMessageContext(),
          viewedData: this.displayedData
        }
      }
    };

    if (lastMessage !== null) {
      payload.replyFor = lastMessage._id;
      payload.replyForContent = getMessageFragment(lastMessage._id);
    }

    if (quotedMessageId !== null) {
      payload.quotedMessageId = quotedMessageId;
    }

    try {

      this.editorSelectionPreview.clearPreview();
      this.clearPreviewState();
      const result = await serverInterface.startup.helpdesk.createOutboundConversationMessage(conversationId, conversationMessageId, payload);

      application.platformUserObserver.pageViewObserver.emit('Assistant messages sent');
      return result;

    } catch (exception) {

      if (exception.code === 'RULE_NOT_MET' && exception.metadata === 'STARTUP_SUBSCRIPTION_MUST_BE_ACTIVE') {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: new UpgradePlanToast(),
          seconds: 10,
          type: 'error'
        });
      } else {
        throw exception;
      }

    }

  }

  async inlineAttachment(conversationId, conversationMessageId, file) {

    return serverInterface.startup.helpdesk.uploadConversationMessageInlineAttachment(conversationId, conversationMessageId, file);

  }

  async uploadAttachment(conversationId, conversationMessageId, file) {

    return serverInterface.startup.helpdesk.uploadConversationMessageAttachment(conversationId, conversationMessageId, file);

  }

  async deleteAttachment(conversationId, conversationMessageId, attachmentId) {

    return serverInterface.startup.helpdesk.deleteAttachment(conversationId, conversationMessageId, attachmentId);

  }

  async handleActivity(conversationId, activity) {

    return serverInterface.startup.helpdesk.postActivity(conversationId, activity);

  }

  setConversation(assistantConversation) {

    if (!assistantConversation) {
      return;
    }

    conversation = assistantConversation;

  }

  async tryLoadConversation() {

    if (conversation !== null) {
      return;
    }

    conversation = await serverInterface.startup.helpdesk.getOrCreateAssistantConversation();

  }

  async tryRenderInternetPhone() {

    if (internetPhone !== null) {
      return;
    }

    this.renderInternetPhone();

  }

  handleClose() {
    this.collapse();
  }

  getNormalizedWidth(width) {

    if (width === '0px') {
      width = '400px';
    }

    return width;

  }

  collapse() {

    this.expanded = false;
    this.el.classList.add('collapsed');
    const container = document.getElementById('assistant-container');

    if (container !== null) {

      localStorage.setItem('assistant-width', this.getNormalizedWidth(container.style.width));
      container.style.width = '0';
    }

    return false;

  }

  expand() {

    this.expanded = true;
    this.el.classList.remove('collapsed');

    const container = document.getElementById('assistant-container');

    if (container !== null) {
      container.style.width = this.getNormalizedWidth(localStorage.getItem('assistant-width'));
    }

    return true;

  }

  toggle() {

    if (this.el.classList.contains('collapsed')) {
      return this.expand();
    } else {
      return this.collapse();
    }

  }

  async loadAssistantData() {

    this.data = await serverInterface.startup.assistants.getAssistantByStartupId();

  }

  async getAssistantData() {

    if (this.data === null) {
      await this.loadAssistantData();
    }

    return this.data;

  }

  async initialize() {

    await Promise.all([
      this.tryLoadConversation(),
      this.getAssistantData()
    ]);

    this.tryRenderInternetPhone();

    setChildren(this.el, [
      el('.assistant-expanded-content', [
        el('#assistant-header-main', [
          el('#assistant-header-body', [
            el('span.startup-icon', [
              el('img', {
                src: 'https://app-assets.venturz.co/assets/images/ask-ai-medium-icon.svg',
                width: 24,
                height: 24
              })
            ]),
            el('label#assistant-header-label', 'Assistant')
          ])
        ]),
        internetPhone
      ])
    ]);

    this.el.classList.remove('loading');

    const container = document.getElementById('assistant-container');

    if (container !== null) {
      container.style.width = localStorage.getItem('assistant-width');
    }

  }

  setText(text = '') {

    const content = [{
      type: 'paragraph',
      padding: [5, 5, 5, 5],
      children: [{ text }]
    }];

    this.setContent(content);

  }

  async setContent(content) {

    await serverInterface.startup.users.setAssistantConversationContent({
      content
    });

    internetPhone.displayLatestDraft();

  }

  onShow() {
    if (!internetPhone) {
      return;
    }
    internetPhone.refreshMessageInput();
    internetPhone.inView();

    const container = document.getElementById('assistant-container');

    if (container !== null) {
      container.style.width = localStorage.getItem('assistant-width');
    }
  }

  displayLatestDraft() {

    return internetPhone.displayLatestDraft();

  }

  update() {

    internetPhone.inView();

  }

  setDisplayedData(data) {

    this.displayedData = data;

  }

  extendDisplayedData(data = {}) {

    this.displayedData = {
      ...(this.displayedData || {}),
      ...data
    };

  }

  clearPreviewState() {

    this.displayedData = {
      ...(this.displayedData || {}),
      blockSelectionAddress: void 0,
      selectedBlocks: void 0
    };

  }

  clearPreview() {

    this.editorSelectionPreview.clearPreview();

  }

  showChangeApplied(changeData) {

    this.editorSelectionPreview.showChangeApplied(changeData);

  }

  previewSelection(nodes) {

    this.editorSelectionPreview.previewSelection(nodes);

  }

  focus() {

    if (this.expanded === false) {
      this.expand();
    }

    internetPhone.focus();

  }

}

export class UpgradePlanToast {

  constructor(configuration) {

    const toast = this;

    this.handle = null;

    this.el = el('.toast-message', [
      el('span', 'You’ve reached your current plan limit. Upgrade to keep using Venturz without any interruption. '),
      el('a', {
        href: '#',
        onclick(event) {
          event.preventDefault();
          event.stopPropagation();

          application.eventSystem.internal.emit(applicationEventCodes.DISPLAY_PROFILE_DIALOG, {
            defaultActiveSectionName: 'Billing',
            disallowCancel: false
          });
          toast.handle.close();
        }
      }, 'Upgrade'),
      el('span', ' to keep using Venturz without any interruption.')
    ]);

  }

  setHandle(handle) {

    this.handle = handle;

  }
}

export default Assistant;
